import { useCallback, useRef, useSyncExternalStore } from 'react'

import type { EditorSelector, EditorSnapshot, EditorStore } from './EditorStore.js'

interface SelectionCache<Value> {
  selector: EditorSelector<Value>
  snapshot: EditorSnapshot | null
  hasValue: boolean
  value: Value | undefined
}

export function useEditorSelector<Value>(store: EditorStore, selector: EditorSelector<Value>): Value {
  const cacheReference = useRef<SelectionCache<Value>>({
    selector,
    snapshot: null,
    hasValue: false,
    value: undefined,
  })
  if (cacheReference.current.selector !== selector) {
    cacheReference.current = {
      selector,
      snapshot: null,
      hasValue: false,
      value: undefined,
    }
  }

  const getSelection = useCallback((): Value => {
    const snapshot = store.getSnapshot()
    const cache = cacheReference.current
    if (cache.snapshot === snapshot && cache.hasValue) return cache.value as Value
    const selected = selector(snapshot)
    cache.snapshot = snapshot
    if (cache.hasValue && Object.is(cache.value, selected)) return cache.value as Value
    cache.hasValue = true
    cache.value = selected
    return selected
  }, [selector, store])

  const subscribe = useCallback(
    (listener: () => void) => store.subscribeSelector(selector, listener),
    [selector, store],
  )

  return useSyncExternalStore(subscribe, getSelection, getSelection)
}
